import React from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, LineElement, PointElement, LinearScale, Title, CategoryScale, Legend, Tooltip } from "chart.js";
import { useTranslation } from "react-i18next";
import { Utensils } from "lucide-react";

// Registra i componenti del grafico
ChartJS.register(LineElement, PointElement, LinearScale, Title, CategoryScale, Legend, Tooltip);

interface DataPoint {
    date: string;
    calories: number;
    proteins: number;
    carbs: number;
    fats: number;
}

interface CaloriesChartProps {
    data: DataPoint[];
}

export const CaloriesChart: React.FC<CaloriesChartProps> = ({ data }) => {
    const { t } = useTranslation();

    // Prepara i dati per il grafico
    const labels = data.map((item) => item.date); // Array di date
    const caloriesData = data.map((item) => item.calories); // Array di calorie
    const proteinsData = data.map((item) => item.proteins);
    const carbsData = data.map((item) => item.carbs);
    const fatsData = data.map((item) => item.fats);

    const chartData = {
        labels,
        datasets: [
            {
                label: t("charts.calories.calories"),
                data: caloriesData,
                borderColor: "rgba(255, 99, 132, 1)",
                backgroundColor: "rgba(255, 99, 132, 0.2)",
                yAxisID: "y1", // Associa alle calorie l'asse Y1
                fill: false,
            },
            {
                label: t("charts.calories.proteins"),
                data: proteinsData,
                borderColor: "rgba(54, 162, 235, 1)",
                backgroundColor: "rgba(54, 162, 235, 0.2)",
                yAxisID: "y2", // Associa a proteins l'asse Y2
                fill: false,
            },
            {
                label: t("charts.calories.carbs"),
                data: carbsData,
                borderColor: "rgba(75, 192, 192, 1)",
                backgroundColor: "rgba(75, 192, 192, 0.2)",
                yAxisID: "y2", // Associa a carbs l'asse Y2
                fill: false,
            },
            {
                label: t("charts.calories.fats"),
                data: fatsData,
                borderColor: "rgba(153, 102, 255, 1)",
                backgroundColor: "rgba(153, 102, 255, 0.2)",
                yAxisID: "y2", // Associa a fats l'asse Y2
                fill: false,
            },
        ],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: "top" as const,
            },
            tooltip: {
                mode: "index" as const,
                intersect: false,
            },
        },
        scales: {
            x: {
                display: true,
                title: {
                    display: true,
                    text: t("charts.calories.xAxis"),
                },
            },
            y1: {
                type: "linear" as const,
                position: "left" as const, // Posiziona sul lato sinistro
                title: {
                    display: true,
                    text: t("charts.calories.yAxisCalories"),
                },
            },
            y2: {
                type: "linear" as const,
                position: "right" as const, // Posiziona sul lato destro
                title: {
                    display: true,
                    text: t("charts.calories.yAxisMacronutrients"),
                },
                grid: {
                    drawOnChartArea: false, // Evita che la griglia del secondo asse si sovrapponga
                },
            },
        },
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold mb-4">{t("charts.calories.title")}</h2>
                <Utensils className="text-primary" size={24} />
            </div>
            <Line data={chartData} options={options} />;
        </div>
    );
};
